import React, { PropsWithChildren, ReactElement } from 'react';
import {
  FieldErrors,
  FieldValues,
  UseControllerProps,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Pill from '@ingka/pill';
import Tooltip from '@ingka/tooltip';
import * as iconsPaths from '@ingka/ssr-icon/icons';

import { toCamel } from '@/lib/utils/string';

type TagOption = {
  value: string;
  i18nKey: string;
  tooltipText?: ReactElement | string;
  iconName?: string;
};

type ArrayFormKey =
  | 'carrier.type'
  | 'carrier.size'
  | 'store.component'
  | 'store.department';

type StringFormKey = 'communicationPurpose' | 'subcategory' | 'permissions';

type AllowedFormKey = ArrayFormKey | StringFormKey;

export type ControlledTagsProps<T extends FieldValues> = {
  name: AllowedFormKey;
  errors: FieldErrors<T>;
  rules?: UseControllerProps<T>['rules'];
  readOnly?: boolean;
  label?: string;
  caption?: string;
  shouldValidate?: boolean;
  successMessage?: string;
  options: TagOption[];
  multiple: boolean;
};

function ControlledTags<T extends FieldValues>(
  props: PropsWithChildren<ControlledTagsProps<T>>
) {
  const { name, label, readOnly, multiple, options = [] } = props;

  // Hooks
  const { t } = useTranslation();
  const { watch, setValue, control } = useFormContext();
  const { append, remove } = useFieldArray({
    control,
    name: name as ArrayFormKey,
  });

  // Event handlers
  const toggleSelection = (value: string) => {
    if (multiple) {
      if (isSelected(value)) {
        const index = findIndex(value);
        remove(index);
      } else {
        append({ value });
      }
    } else {
      setValue(name, value, { shouldDirty: true, shouldValidate: true });
    }
  };

  // Data
  const selectedItems = watch(name);

  const findIndex = (value: string): number => {
    if (multiple && Array.isArray(selectedItems)) {
      for (let i = 0; i < selectedItems.length; i++) {
        if (selectedItems[i].value == value) return i;
      }
    }
    return -1;
  };

  const isSelected = (value: string): boolean => {
    if (multiple) {
      return findIndex(value) >= 0;
    }
    return selectedItems === value;
  };

  return (
    <>
      {label && <label>{label}</label>}
      <div className="my-3 flex flex-wrap gap-[10px]">
        {options.map(({ value, i18nKey, tooltipText, iconName }) => {
          const ssrIcon = iconName
            ? iconsPaths[toCamel(iconName) as keyof typeof iconsPaths]
            : undefined;
          const pillContent = (
            <Pill
              key={value}
              label={
                <p className="flex flex-row items-center gap-1">
                  {iconName && !ssrIcon && (
                    <img
                      src={`/assets/icons/${iconName}.svg`}
                      alt=""
                      width={20}
                      height={20}
                    />
                  )}
                  <span>{t(i18nKey)}</span>
                </p>
              }
              selected={isSelected(value)}
              size="small"
              onClick={() => toggleSelection(value)}
              disabled={!isSelected(value) && readOnly}
              className={readOnly ? 'cursor-default pointer-events-none' : ''}
              data-testid={`pill-${value}`}
              ssrIcon={ssrIcon}
            />
          );

          if (tooltipText) {
            return (
              <Tooltip
                key={value}
                position="bottom"
                // @ts-ignore
                tooltipText={tooltipText}
              >
                {pillContent}
              </Tooltip>
            );
          }

          return pillContent;
        })}
      </div>
    </>
  );
}

export default ControlledTags;
